import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { format, subDays } from "date-fns";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { DataGrid } from "@mui/x-data-grid";
import "../pages/ZReport.css";

const safeArray = (val) => {
  try {
    if (!val) return [];
    return Array.isArray(val) ? val : JSON.parse(val || "[]");
  } catch {
    return [];
  }
};

export default function ZReportNext() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);

  const [oneStop, setOneStop] = useState(null);
  const [snappy, setSnappy] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [existingZRow, setExistingZRow] = useState(null);

  const [staffTillCash, setStaffTillCash] = useState("");
  const [staffSnappyCash, setStaffSnappyCash] = useState("");
  const [staffTotalCashToday, setStaffTotalCashToday] = useState("");
  const [staffCashInShop, setStaffCashInShop] = useState("");
  const [snappyCashRefunds, setSnappyCashRefunds] = useState("");

  const [zReports, setZReports] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchSupplier, setSearchSupplier] = useState("");
  const [editId, setEditId] = useState(null);

  const prevDateObj = subDays(new Date(date + "T00:00:00"), 1);
  const prevDateLabel = format(prevDateObj, "dd/MM/yyyy");
  const currentDateLabel = format(new Date(date + "T00:00:00"), "dd/MM/yyyy");
  const startDate = format(subDays(new Date(date + "T00:00:00"), 6), "yyyy-MM-dd");
const endDate = date;
const yesterday = format(subDays(new Date(date + "T00:00:00"), 1), "yyyy-MM-dd");
const last7Rows = zReports
  .filter((r) => r.date >= startDate && r.date <= endDate)
  .sort((a, b) => {
    // yesterday always on top
    if (a.date === yesterday && b.date !== yesterday) return -1;
    if (b.date === yesterday && a.date !== yesterday) return 1;

    // then today
    if (a.date === date && b.date !== date) return -1;
    if (b.date === date && a.date !== date) return 1;

    // otherwise newest first
    return b.date.localeCompare(a.date);
  });

  const loadData = async () => {
    try {
      const { data: oneStopData } = await supabase
        .from("onestop_extracted")
        .select("*")
        .eq("report_date", date)
        .maybeSingle();
      setOneStop(oneStopData);

      const { data: snappyData } = await supabase
        .from("snappy_extracted")
        .select("*")
        .eq("report_date", date)
        .maybeSingle();
      setSnappy(snappyData);

      const { data: supplierData } = await supabase
        .from("suppliers")
        .select("*")
        .eq("date", date);
      setSuppliers(supplierData || []);

      const { data: todayRow } = await supabase
        .from("zreports")
        .select("*")
        .eq("date", date)
        .maybeSingle();
      setExistingZRow(todayRow || null);

      if (todayRow) {
        setStaffTillCash(todayRow.staff_till_cash?.toString() ?? "");
        setStaffSnappyCash(todayRow.staff_snappy_cash?.toString() ?? "");
        setStaffTotalCashToday(todayRow.staff_total_cash_today?.toString() ?? "");
        setStaffCashInShop(todayRow.staff_cash_in_shop?.toString() ?? "");
        setSnappyCashRefunds(todayRow.snappy_cash_refunds?.toString() ?? "");
        setEditId(todayRow.id || null);
      } else {
        resetForm();
      }

      let q = supabase.from("zreports").select("*").order("date", { ascending: true });
      if (fromDate) q = q.gte("date", fromDate);
      if (toDate) q = q.lte("date", toDate);
      const { data: allReports } = await q;
      setZReports(allReports || []);
    } catch (err) {
      toast.error("❌ Load failed: " + err.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [date, fromDate, toDate]);

  const resetForm = () => {
    setStaffTillCash("");
    setStaffSnappyCash("");
    setStaffTotalCashToday("");
    setStaffCashInShop("");
    setSnappyCashRefunds("");
    setEditId(null);
  };

  const supplierCashOut = suppliers
    .filter((s) => s.method === "Cash")
    .reduce((sum, s) => sum + Number(s.total_amount || 0), 0);

  const supplierBankOut = suppliers
    .filter((s) => ["Bank", "BACS"].includes(s.method))
    .reduce((sum, s) => sum + Number(s.total_amount || 0), 0);

  const totalSnappyCash = (snappy?.cash_sales || 0) + (snappy?.snappy_customer_fee || 0);
  const cashIn = (oneStop?.cash_sales || 0) + totalSnappyCash;

  const yesterdayRow = (zReports || []).filter(r => r.date < date).pop();
  const yesterdayBalance = yesterdayRow ? yesterdayRow.cash_balance : 0;

  const expectedSystemBalance = Number(yesterdayBalance || 0) + cashIn - supplierCashOut;
  const  system_expected_cash_in_shop = Number(yesterdayRow?.staff_cash_in_shop || 0)  + cashIn  - supplierCashOut;
  

  const staffCashToday = Number(staffTillCash || 0) + Number(staffSnappyCash || 0);
 
  const oneStopCashVariance = Number(staffTillCash || 0) - Number(oneStop?.cash_sales || 0);
  const snappyCashVariance = Number(staffSnappyCash || 0) - Number(totalSnappyCash || 0);
  const todayDailyVariance = (Number(staffTotalCashToday) || staffCashToday) - cashIn;
  const todayShopVariance = Number(staffCashInShop || 0) - expectedSystemBalance;
  const todaySnappySalesVariance =(snappy?.total_product_sales || 0) - (oneStop?.cheque_sales || 0) ;

  const money = (n) => `£${Number(n || 0).toFixed(2)}`;

  const handleSave = async () => {
    try {
      if (Number(snappyCashRefunds) < 0) {
        toast.error("❌ Snappy Cash Refunds cannot be negative.");
        return;
      }

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user?.user_metadata?.display_name || "Unknown";

      const suppliersJson = suppliers.map((s) => ({
        name: s.supplier_name,
        amount: s.total_amount,
        method: s.method,
      }));

      const payload = {
        date,
        one_stop_total_sales: oneStop?.z_reading_total || 0,
        one_stop_card: oneStop?.card_sales || 0,
        one_stop_cash: oneStop?.cash_sales || 0,
        snappy_card: snappy?.card_sales || 0,
        snappy_cash: totalSnappyCash,
        cash_in: cashIn,
        justeat_card: oneStop?.just_eat || 0,
        justeat_cash: 0,
        deliveroo_card: oneStop?.deliveroo || 0,
        deliveroo_cash: 0,
        delivery_fee: snappy?.delivery_charge || 0,
        supplier_cash_out: supplierCashOut,
        supplier_bank_out: supplierBankOut,
        net_cash: cashIn - supplierCashOut,
        cash_balance: expectedSystemBalance,
        variance: todayDailyVariance,
        snappy_variance: todaySnappySalesVariance,
        staff_till_cash: Number(staffTillCash) || 0,
        staff_snappy_cash: Number(staffSnappyCash) || 0,
        staff_total_cash_today: staffTotalCashToday !== "" ? Number(staffTotalCashToday) : staffCashToday,
        staff_cash_in_shop: Number(staffCashInShop) || 0,
        snappy_total_product_sales: snappy?.total_product_sales || 0,
        snappy_total_surcharges: snappy?.total_surcharges || 0,
        snappy_refunds: snappy?.refunds || 0,
        snappy_cash_refunds: Math.max(0, Number(snappyCashRefunds) || 0),
        snappy_total_product_sales_from_till: oneStop?.cheque_sales || 0,
        snappy_daily_till_sales: oneStop?.cheque_sales || 0,
        snappy_total_refunds: snappy?.refunds || 0,
        suppliers: suppliersJson,
        logged_by: editId ? undefined : user,
        logged_at: editId ? undefined : new Date().toISOString(),
        modified_by: editId ? user : undefined,
        modified_at: editId ? new Date().toISOString() : undefined,
      };

      if (editId) payload.id = editId;

      const { error } = await supabase.from("zreports").upsert(payload);
      if (error) throw error;

      toast.success(editId ? "✅ ZReport updated!" : "✅ ZReport saved!");
      if (!editId) resetForm();
      await loadData();

    } catch (err) {
      toast.error("❌ Save failed: " + err.message);
    }
  };

  const columns = [
    { field: "date", headerName: "Date", width: 110 },
    { field: "one_stop_total_sales", headerName: "One Stop Total Sales (£)", width: 140,
      renderCell: (p) => <span className="red">{money(p.value)}</span> }, 
    { field: "one_stop_card", headerName: "One Stop Card (£)", width: 150,
      renderCell: (p) => <span className="blue">{money(p.value)}</span> },
    { field: "one_stop_cash", headerName: "One Stop Cash (£)", width: 150,
      renderCell: (p) => <span className="green">{money(p.value)}</span> },
    { field: "snappy_card", headerName: "Snappy Card (£)", width: 150,
      renderCell: (p) => <span className="blue">{money(p.value)}</span> },
    { field: "snappy_cash", headerName: "Snappy Cash (£)", width: 150,
      renderCell: (p) => <span className="green">{money(p.value)}</span> },
    { field: "cash_in", headerName: "System Cash In (£)", width: 140,
      renderCell: (p) => <span className="orange">{money(p.value)}</span> },
    { field: "staff_till_cash", headerName: "Till Cash (Staff) (£)", width: 140,
      renderCell: (p) => <span className="pink">{money(p.value)}</span> },
    { field: "staff_snappy_cash", headerName: "Snappy Cash (Staff) (£)", width: 140,
      renderCell: (p) => <span className="pink">{money(p.value)}</span> },
    { field: "staff_total_cash_today", headerName: "Staff Total Cash Today (£)", width: 140,
      renderCell: (p) => <span className="pink">{money(p.value)}</span> },
    { field: "cash_balance", headerName: "System Cash Balance (£)", width: 150,
      renderCell: (p) => <span className="orange">{money(p.value)}</span> },
    { field: "staff_cash_in_shop", headerName: "Cash in Shop (Staff) (£)", width: 150,
      renderCell: (p) => <span className="pink">{money(p.value)}</span> },
    { field: "variance", headerName: "Daily Variance (£)", width: 170,
      renderCell: (p) =>
        <span style={{ color: Number(p.value) === 0 ? "green" : "crimson", fontWeight: 600 }}>
          {Number(p.value) === 0 ? "Matched" : money(p.value)}
        </span> },
            { field: "snappy_total_product_sales_from_till", headerName: "Snappy Till Sales( Cheque)", width: 150,
      renderCell: (p) => <span className="yellow">{money(p.value)}</span> },
            { field: "snappy_total_product_sales", headerName: "Snappy Total Product Sales (£)", width: 140,
      renderCell: (p) => <span className="yellow">{money(p.value)}</span> },
    { field: "snappy_variance", headerName: "Snappy Sales Variance (£)", width: 140,
      renderCell: (p) =>
        <span style={{ color: Number(p.value) === 0 ? "green" : "crimson", fontWeight: 600 }}>
          {Number(p.value) === 0 ? "Matched" : money(p.value)}
        </span> },
    { field: "justeat_card", headerName: "Just Eat (£)", width: 150,
      renderCell: (p) => <span className="blue">{money(p.value)}</span> },
    { field: "deliveroo_card", headerName: "Deliveroo (£)", width: 160,
      renderCell: (p) => <span className="blue">{money(p.value)}</span> },
    { field: "delivery_fee", headerName: "Delivery Fee (£)", width: 160,
      renderCell: (p) => <span className="green">{money(p.value)}</span> },
    { field: "supplier_cash_out", headerName: "Supplier Cash Out (£)", width: 140,
      renderCell: (p) => <span className="red">{money(p.value)}</span> },
    { field: "supplier_bank_out", headerName: "Supplier Bank Out (£)", width: 140,
      renderCell: (p) => <span className="blue">{money(p.value)}</span> },
    { field: 'suppliers', headerName: 'Suppliers', width: 250, renderCell: p => (p.value || []).map(s => `${s.name} £${s.amount} (${s.method})`).join(", ") },
    { field: "logged_by", headerName: "Logged By", width: 140 },
    { field: "logged_at", headerName: "Logged At", width: 170 },
    { field: "modified_by", headerName: "Modified By", width: 140 },
    { field: "modified_at", headerName: "Modified At", width: 170 },
    {
      field: "actions",
      headerName: "⚙️ Actions",
      width: 160,
      sortable: false,
      renderCell: (p) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn-edit" onClick={() => {
            setDate(p.row.date);
            setStaffTillCash(p.row.staff_till_cash?.toString() || "");
            setStaffSnappyCash(p.row.staff_snappy_cash?.toString() || "");
            setStaffTotalCashToday(p.row.staff_total_cash_today?.toString() || "");
            setStaffCashInShop(p.row.staff_cash_in_shop?.toString() || "");
            setSnappyCashRefunds(p.row.snappy_cash_refunds?.toString() || "");
            setEditId(p.row.id);
          }}>Edit</button>
          <button className="btn-delete" onClick={async () => {
            const confirm = prompt("Type DELETE to confirm");
            if (confirm !== "DELETE") return;
            const { error } = await supabase.from("zreports").delete().eq("id", p.row.id);
            if (error) toast.error("❌ Delete failed: " + error.message);
            else {
              toast.success("✅ Deleted");
              await loadData();
            }
          }}>Delete</button>
        </div>
      )
    },
  ];

  return (
    <div className="zreport-container">
      <div className="main-heading">🆕 Store ZReport Next</div>

      <div className="form-section">
        <div className="section-title">📅 Date</div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      {/* Yesterday Recap */}
      <div className="form-section">
        <div className="section-title">🕘 Yesterday Recap ({prevDateLabel})</div>
        <div className="form-grid">
          <label>System Cash In (Yesterday)</label>
          <div className="green"><b>{money(yesterdayRow?.cash_in || 0)}</b></div>
          <label>System Cash Balance (End of Day)</label>
          <div className="green"><b>{money(yesterdayRow?.cash_balance || 0)}</b></div>
          <label>Staff Cash Counted (Yesterday)</label>
          <div className="pink"><b>{money(yesterdayRow?.staff_total_cash_today || 0)}</b></div>
          <label>Staff Cash in Shop (Yesterday)</label>
          <div className="pink"><b>{money(yesterdayRow?.staff_cash_in_shop || 0)}</b></div>
          <label>Variance (System vs Staff)</label>
          <div className={Number(yesterdayRow?.variance || 0) === 0 ? "green" : "red"}>
            <b>{Number(yesterdayRow?.variance || 0) === 0 ? "Matched" : money(yesterdayRow?.variance || 0)}</b>
          </div>
          <label>Snappy Sales Variance</label>
          <div className={Number(yesterdayRow?.snappy_variance || 0) === 0 ? "green" : "red"}>
            <b>{Number(yesterdayRow?.snappy_variance || 0) === 0 ? "Matched" : money(yesterdayRow?.snappy_variance || 0)}</b>
          </div>
        </div>
      </div>


      {/* One Stop */}
      <div className="form-section">
          <div className="section-title">🛍️ One Stop Transactions ({prevDateLabel})</div>
          <div className="form-grid">     
       
          <label>Total Sales</label><div><b>{money(oneStop?.z_reading_total)}</b></div>
          <label>Total Card Sales</label><div className="blue"><b>{money(oneStop?.card_sales)}</b></div>
          <label>Total Cash Sales</label><div className="green"><b>{money(oneStop?.cash_sales)}</b></div>
          <label>Vouchers</label><div><b>{money(oneStop?.vouchers)}</b></div>
          <label>Snappy Sales (Cheque)</label><div><b>{money(oneStop?.cheque_sales)}</b></div>
        </div>
      </div>
      


      {/* Online */}
        <div className="form-section">
         <div className="section-title">🌐 Online Transactions({currentDateLabel})</div>
        <div className="form-grid">
         <label>Snappy Card Sales</label><div className="blue"><b>{money(snappy?.card_sales)}</b></div>
          <label>Snappy Cash Sales</label><div className="green"><b>{money(totalSnappyCash)}</b></div>
          <label>Refunds</label><div><b>{money(snappy?.refunds)}</b></div>
          <label>Delivery Charge</label><div className="green"><b>{money(snappy?.delivery_charge)}</b></div>
          
          <label>Total Product Sales</label><div><b>{money(snappy?.total_product_sales)}</b></div>
          <label>Net Sales</label><div><b>{money(snappy?.net_sales)}</b></div>
          <label>Just Eat</label><div className="blue"><b>{money(oneStop?.just_eat)}</b></div>
          <label>Deliveroo</label><div className="blue"><b>{money(oneStop?.deliveroo)}</b></div>
        </div>
      </div>

      {/* Summary */}
      <div className="form-section">
        <div className="section-title">📌 Summary ({currentDateLabel})</div>
        <div className="form-grid">
          <label>System Total Cash In</label><div className="green"><b>{money(cashIn)}</b></div>
          <label>System Expected Balance</label><div className="green"><b>{money(expectedSystemBalance)}</b></div>
          <label>System Expected Cash In Shop</label><div className="pink"><b>{money(system_expected_cash_in_shop)}</b></div>
          <label>Supplier Cash Out Today</label><div className="red"><b>{money(supplierCashOut)}</b></div>
          <label>Supplier Bank Out Today</label><div className="blue"><b>{money(supplierBankOut)}</b></div>
          <label>Staff Cash Counted Today</label><div className="green"><b>{money(Number(staffTotalCashToday) || staffCashToday)}</b></div>
          <label>Staff Cash in Shop Today</label><div className="green"><b>{money(staffCashInShop)}</b></div>
        </div>
      </div>

      {/* Variance */}
      <div className="form-section">
        <div className="section-title">📅 Variance ({currentDateLabel})</div>
        <div className="form-grid">
          <label>One Stop Cash Variance</label>
          <div className={oneStopCashVariance === 0 ? "green" : "red"}>
            <b>{oneStopCashVariance === 0 ? "Matched" : money(oneStopCashVariance)}</b>
          </div>
         <label>Snappy Cash Variance</label>
        <div className={Math.abs(snappyCashVariance) < 0.01 ? "green" : "red"}>
  <b>{Math.abs(snappyCashVariance) < 0.01 ? "Matched" : money(snappyCashVariance)}</b>
        </div>
          <label>Total Cash Variance (System)</label>
          <div className={todayDailyVariance === 0 ? "green" : "red"}>
            <b>{todayDailyVariance === 0 ? "Matched" : money(todayDailyVariance)}</b>
          </div>
          <label>Total Cash Variance (Staff)</label>
          <div className={todayShopVariance === 0 ? "green" : "red"}>
            <b>{todayShopVariance === 0 ? "Matched" : money(todayShopVariance)}</b>
          </div>
          <label>Snappy Sales Variance</label>
          <div className={todaySnappySalesVariance === 0 ? "green" : "red"}>
            <b>{todaySnappySalesVariance === 0 ? "Matched" : money(todaySnappySalesVariance)}</b>
          </div>
        </div>
      </div>

      {/* Staff Input */}
      <div className="form-section">
        <div className="section-title">👩‍💼 Staff Input</div>
        <div className="form-grid">
          <label>Till Cash (£)
            <input type="number" value={staffTillCash} onChange={(e) => setStaffTillCash(e.target.value)} />
          </label>
          <label>Snappy Cash (£)
            <input type="number" value={staffSnappyCash} onChange={(e) => setStaffSnappyCash(e.target.value)} />
          </label>
          <label>Total Cash Count Today (£)
            <input type="number" value={staffTotalCashToday} onChange={(e) => setStaffTotalCashToday(e.target.value)} />
          </label>
          <label>Total Cash in Shop Included Today (£)
            <input type="number" value={staffCashInShop} onChange={(e) => setStaffCashInShop(e.target.value)} />
          </label>
          <label>Snappy Cash Refunds (£)
            <input type="number" value={snappyCashRefunds} onChange={(e) => setSnappyCashRefunds(e.target.value)} />
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="form-section">
        <div className="section-title">⚙️ Actions</div>
        <div className="form-grid">
          <button className="btn-entry" onClick={handleSave}>
            {editId ? "Update ZReport" : "Save ZReport"}
          </button>
          <button className="btn-export" onClick={() => {
       const headers = columns.map(c => c.headerName).join(",");

const rows = zReports.map(r =>
  columns.map(c => {
    let val = r[c.field];

    if (c.field === "suppliers") {
      const arr = safeArray(val);
      return arr.map(s => `${s.name}:${s.amount}(${s.method})`).join(" | ");
    }

    if (c.field === "logged_at" || c.field === "modified_at") {
      return val ? new Date(val).toLocaleString() : "";
    }

    // Default
    return typeof val === "object" ? JSON.stringify(val) : (val ?? "");
  }).join(",")
);

const blob = new Blob([headers + "\n" + rows.join("\n")], { type: "text/csv" });
saveAs(blob, `zreport_next_${format(new Date(),"yyyyMMdd_HHmm")}.csv`);

          }}>
            Export CSV
          </button>
          <label>From Date
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </label>
          <label>To Date
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </label>
          <label>Filter by Supplier
            <input type="text" value={searchSupplier} onChange={(e) => setSearchSupplier(e.target.value)} />
          </label>
          <button className="btn-apply" onClick={loadData}>Apply Filters</button>
        </div>
      </div>

      {/* DataGrid */}
      <div style={{ marginTop: 24 }}>
        <h3>📜 ZReport Summary</h3>
        <DataGrid
rows={last7Rows.filter((r) =>
  searchSupplier
    ? (() => {
        const arr = safeArray(r.suppliers);
        return arr
          .map((s) => (s.name || "").toLowerCase())
          .join(",")
          .includes(searchSupplier.toLowerCase());
      })()
    : true
)}
sx={{
    border: "1px solid #ccc",
    "& .MuiDataGrid-cell": {
      borderRight: "1px solid #ddd !important",
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "block !important",
    },
    "& .MuiDataGrid-row": {
      borderBottom: "1px solid #eee !important",
    },
  }}
          columns={columns}
          getRowId={(row) => row?.id ?? row?.date ?? Math.random().toString(36).substr(2,9)}
          pageSize={7}
          autoHeight
        />
      </div>
    </div>
  );
}
